import React, { useEffect, useState, useRef } from 'react'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import { connect } from 'react-redux'
import Avatar from '../components/Avatar'
import { reorderCharacters, reorderInitiativeQueueItems, updateCharacter, updateInitiativeQueue } from '../../actions'
import * as characterService from '../../services/characters'
import * as customEntryService from '../../services/customInitiativeEntries'
import { DiceRoll } from 'rpg-dice-roller'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import IconButton from '@material-ui/core/IconButton'
import AddIcon from '@material-ui/icons/Add'

import './Party.css'

const grid = 8;

const getItemStyle = (isDragging, draggableStyle) => ({
  // some basic styles to make the items look a bit nicer
  userSelect: 'none',
  padding: '16px 16px 8px 16px',
  margin: `0 ${grid}px ${grid}px 0`,

  // change background colour if dragging
  background: isDragging ? '#F79639' : '#222',
  borderRadius: '4px',

  // styles we need to apply on draggables
  ...draggableStyle,
});

const getListStyle = (isDraggingOver, isViewer) => ({
  // background: isDraggingOver ? 'lightblue' : 'lightgrey',
  display: 'flex',
  flexWrap: isViewer ? 'wrap' : 'nowrap',
  padding: grid,
  overflow: isViewer ? 'visible' : 'auto',
});

const Party = (props) => {
  const {
    characters,
    ownerId,
    token,
    reorderInitiativeQueueItems,
    updateCharacter,
    initiativeQueueItems,
    initiativeQueue,
    characterId,
    gameId,
    customInitiativeEntries,
    isViewer,
  } = props

  const [avatarScale, setAvatarScale] = useState(1.0);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newEntryName, setNewEntryName] = useState('');
  const [newEntryQuantity, setNewEntryQuantity] = useState(1);
  const [newEntryInitiative, setNewEntryInitiative] = useState('1d20');
  const [newEntryIcon, setNewEntryIcon] = useState('');
  const [newEntryNotes, setNewEntryNotes] = useState('');
  const [newEntryError, setNewEntryError] = useState('');
  const [editingCustomEntryId, setEditingCustomEntryId] = useState(null);
  const [editingCustomEntryValue, setEditingCustomEntryValue] = useState('');

  const openAddDialog = () => {
    setShowAddDialog(true);
    setNewEntryError('');
  };

  // Refs for dialog fields
  const nameFieldRef = useRef(null);
  const quantityFieldRef = useRef(null);
  const initiativeFieldRef = useRef(null);
  const iconFieldRef = useRef(null);

  const handleIncrementSize = () => {
    setAvatarScale(prevScale => Math.min(prevScale + 0.1, 2.0));
  };

  const handleDecrementSize = () => {
    setAvatarScale(prevScale => Math.max(prevScale - 0.1, 0.5));
  };

  const handleUpdateInitiative = (characterId, newInitiative) => {
    characterService.updateInitiativeValue(gameId, initiativeQueue.id, characterId, newInitiative, token)
      .catch(err => console.error('Failed to update initiative:', err));
  };

  const handleCreateCustomEntry = () => {
    if (!newEntryName) return; // Don't submit if name is empty

    const quantity = parseInt(newEntryQuantity) || 1;
    const isDiceFormula = typeof newEntryInitiative === 'string' && /\d*d\d+/i.test(newEntryInitiative);

    // Helper to evaluate initiative value
    const getInitiativeValue = () => {
      if (isDiceFormula) {
        try {
          const roll = new DiceRoll(newEntryInitiative);
          return roll.total;
        } catch (e) {
          return parseInt(newEntryInitiative) || 0;
        }
      } else {
        return parseInt(newEntryInitiative) || 0;
      }
    };

    // Create entries using batch endpoint
    const createEntries = async () => {
      const entries = [];

      for (let i = 1; i <= quantity; i++) {
        const entryName = quantity > 1 ? `${newEntryName} ${i}` : newEntryName;
        const initiativeValue = getInitiativeValue(); // Roll separately for each entry

        entries.push({
          name: entryName,
          initiative: initiativeValue,
          icon_url: newEntryIcon,
          notes: newEntryNotes
        });
      }

      try {
        await customEntryService.batchCreate(gameId, entries, token);

        // Close dialog and reset
        setShowAddDialog(false);
        setNewEntryName('');
        setNewEntryQuantity(1);
        setNewEntryInitiative('1d20');
        setNewEntryIcon('');
        setNewEntryNotes('');
        setNewEntryError('');
      } catch (err) {
        console.error('Failed to create custom entries:', err);
        // Show error message to user
        const errorMessage = err.error || err.message || 'Failed to create entries';
        setNewEntryError(errorMessage);
      }
    };

    createEntries();
  };

  const handleDialogKeyDown = (e, currentField) => {
    if (e.key === 'Enter' && newEntryName) {
      e.preventDefault();
      handleCreateCustomEntry();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (currentField === 'name') quantityFieldRef.current?.focus();
      else if (currentField === 'quantity') initiativeFieldRef.current?.focus();
      else if (currentField === 'initiative') iconFieldRef.current?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (currentField === 'quantity') nameFieldRef.current?.focus();
      else if (currentField === 'initiative') quantityFieldRef.current?.focus();
      else if (currentField === 'icon') initiativeFieldRef.current?.focus();
    }
  };

  const handleDeleteCustomEntry = (entryId) => {
    customEntryService.destroy(gameId, entryId, token)
      .catch(err => console.error('Failed to delete custom entry:', err));
  };

  const handleEditCustomEntry = (entryId, currentInitiative) => {
    if (characterId === ownerId) {
      setEditingCustomEntryId(entryId);
      setEditingCustomEntryValue(currentInitiative);
    }
  };

  const handleSaveCustomEntry = () => {
    const parsed = parseInt(editingCustomEntryValue);
    if (!isNaN(parsed) && editingCustomEntryId) {
      customEntryService.update(gameId, editingCustomEntryId, { initiative: parsed }, token)
        .catch(err => console.error('Failed to update custom entry:', err));
    }
    setEditingCustomEntryId(null);
    setEditingCustomEntryValue('');
  };

  const handleUpdateHealthStatus = (entryId, healthStatus) => {
    customEntryService.update(gameId, entryId, { health_status: healthStatus }, token)
      .catch(err => console.error('Failed to update health status:', err));
  };

  const getHealthColor = (healthStatus) => {
    switch (healthStatus) {
      case 'healthy': return '#4caf50'; // Green
      case 'bloodied': return '#ffeb3b'; // Yellow
      case 'ravaged': return '#f44336'; // Red
      default: return '#4caf50'; // Default to green
    }
  };

  useEffect(() => {
    const handleKeyPress = (event) => {
      // Don't intercept when user is typing in an input
      const tag = event.target.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return

      // Only handle space key and only if user is GM
      if (event.code === 'Space' && characterId === ownerId) {
        event.preventDefault(); // Prevent page scroll

        // Send request to update current position
        fetch(`/games/${gameId}/initiative_queues/next_turn`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': token,
          },
        });
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [characterId, ownerId, gameId, token]);

  const onDragEnd = (result) => {
    if (characterId !== ownerId) {
      return;
    }

    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) {
      return;
    }

    // Create the new ordering from the drag and drop
    const newOrderedIds = [...initiativeQueueItems.orderedItemIds]
    const [movedId] = newOrderedIds.splice(sourceIndex, 1)
    newOrderedIds.splice(destinationIndex, 0, movedId)

    // Clone the moved item to avoid mutating Redux state
    const movedItem = { ...initiativeQueueItems.items[movedId] }
    const movedChar = movedItem.queueable_type === 'Character'
      ? { ...characters[movedItem.queueable_id] }
      : null;

    // If the item is being moved to the current position and is hidden, unhide it
    const isCurrentTurn = initiativeQueue.current_position === destinationIndex || (initiativeQueue.current_position === 0 && destinationIndex === 0);
    if (isCurrentTurn && movedItem.hidden) {
      movedItem.hidden = false;
    }

    // Clone all items and characters we'll be modifying
    const clonedItems = {}
    const clonedChars = {}
    newOrderedIds.forEach((id) => {
      clonedItems[id] = id === movedId ? movedItem : { ...initiativeQueueItems.items[id] }
      const item = clonedItems[id]
      if (item.queueable_type === 'Character') {
        clonedChars[item.queueable_id] = item.queueable_id === movedItem.queueable_id && movedChar
          ? movedChar
          : { ...characters[item.queueable_id] }
      }
    })

    // Calculate new initiative based on drop position
    let newInitiative
    if (destinationIndex === 0) {
      const oldFirst = initiativeQueueItems.items[initiativeQueueItems.orderedItemIds[0]]
      newInitiative = oldFirst.initiative + 1
    } else if (destinationIndex === newOrderedIds.length - 1) {
      const priorLastItem = initiativeQueueItems.items[initiativeQueueItems.orderedItemIds[initiativeQueueItems.orderedItemIds.length - 1]]
      newInitiative = priorLastItem.initiative - 1
    } else {
      const itemBehind = clonedItems[newOrderedIds[destinationIndex - 1]]
      newInitiative = itemBehind.initiative
    }

    movedItem.initiative = newInitiative
    if (movedChar) {
      movedChar.initiative = newInitiative
    }

    // Calculate initiative priorities on cloned objects
    let prevItem = null;
    newOrderedIds.forEach((id) => {
      const item = clonedItems[id];

      if (prevItem == null) {
        item.initiative_priority = 0;
      } else if (item.initiative === prevItem.initiative) {
        item.initiative_priority = prevItem.initiative_priority + 1;
      } else {
        item.initiative_priority = 0;
      }

      if (item.queueable_type === 'Character') {
        const char = clonedChars[item.queueable_id];
        if (char) {
          char.initiative_priority = item.initiative_priority;
        }
      }

      prevItem = item;
    });

    // Dispatch updates and persist to server, but only for items that actually changed
    newOrderedIds.forEach((id) => {
      const item = clonedItems[id];
      const originalItem = initiativeQueueItems.items[id];
      if (item.initiative === originalItem.initiative && item.initiative_priority === originalItem.initiative_priority) {
        return;
      }

      if (item.queueable_type === 'Character') {
        const char = clonedChars[item.queueable_id];
        if (char) {
          updateCharacter(char)
          characterService.updateInitiative(char.initiative, char.initiative_priority, char.id, token)
            .catch(err => console.error('Failed to update character initiative:', err))
        }
      } else if (item.queueable_type === 'CustomInitiativeEntry') {
        customEntryService.update(gameId, item.queueable_id, {
          initiative: item.initiative,
          initiative_priority: item.initiative_priority
        }, token).catch(err => console.error('Failed to update custom entry:', err))
      }
    })

    // Build updated initiative queue items from clones
    const updatedInitiativeQueueItems = { ...initiativeQueueItems.items }
    newOrderedIds.forEach((id) => {
      updatedInitiativeQueueItems[id] = clonedItems[id]
    })

    reorderInitiativeQueueItems(updatedInitiativeQueueItems)
  };

  return(
    <div className='Party'>
      <div className='Party__header'>
        <span>Party</span>
        <div className='Party__sizeControls'>
          {characterId === ownerId && (
            <IconButton
              onClick={openAddDialog}
              title="Add custom initiative entry"
              style={{ color: '#fff', padding: '4px', marginRight: '8px' }}
              size="small"
            >
              <AddIcon fontSize="small" />
            </IconButton>
          )}
          <button
            className='Party__sizeButton'
            onClick={handleDecrementSize}
            title="Decrease avatar size"
          >
            -
          </button>
          <span className='Party__sizeLabel'>{Math.round(avatarScale * 100)}%</span>
          <button
            className='Party__sizeButton'
            onClick={handleIncrementSize}
            title="Increase avatar size"
          >
            +
          </button>
        </div>
      </div>
      <div className='Party__characters'>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable" direction="horizontal">
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                style={getListStyle(snapshot.isDraggingOver, isViewer)}
                {...provided.droppableProps}
              >
                {initiativeQueueItems.orderedItemIds.map((id, index) => {
                  const queueItem = initiativeQueueItems.items[id]

                  // Skip rendering if the item is hidden and the viewer is not the owner
                  if (queueItem.hidden && characterId !== ownerId) {
                    return null;
                  }

                  const isCurrentTurn = initiativeQueue.current_position === index || (initiativeQueue.current_position === 0 && index === 0);

                  // Handle Character queue items
                  if (queueItem.queueable_type === 'Character') {
                    const char = characters[queueItem.queueable_id];
                    if (!char) {
                      return null;
                    }

                    return (
                      <Draggable key={`char-${char.id}`} draggableId={`char-${char.id}`} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={getItemStyle(
                              snapshot.isDragging,
                              provided.draggableProps.style
                            )}
                            className={`Party__item ${isCurrentTurn ? 'Party__item--current-turn' : ''}`}
                          >
                            <Avatar
                              key={char.id}
                              id={char.id}
                              name={char.name}
                              ownerId={ownerId}
                              isOwner={props.isOwner}
                              avatarUrl={char.avatar_url}
                              initiative={char.initiative}
                              initiativePriority={char.initiative_priority}
                              kind='party'
                              scale={avatarScale}
                              isGM={characterId === ownerId}
                              onUpdateInitiative={handleUpdateInitiative}
                              isHidden={queueItem.hidden}
                            />
                          </div>
                        )}
                      </Draggable>
                    )
                  }

                  // Handle CustomInitiativeEntry queue items
                  if (queueItem.queueable_type === 'CustomInitiativeEntry') {
                    const entry = customInitiativeEntries[queueItem.queueable_id];
                    if (!entry) {
                      return null;
                    }

                    const isEditing = editingCustomEntryId === entry.id;
                    const healthColor = getHealthColor(entry.health_status || 'healthy');

                    return (
                      <Draggable key={`custom-${entry.id}`} draggableId={`custom-${entry.id}`} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={getItemStyle(
                              snapshot.isDragging,
                              provided.draggableProps.style
                            )}
                            className={`Party__item ${isCurrentTurn ? 'Party__item--current-turn' : ''}`}
                            onDoubleClick={() => handleEditCustomEntry(entry.id, entry.initiative)}
                          >
                            <div style={{
                              textAlign: 'center',
                              position: 'relative',
                              opacity: (characterId === ownerId && queueItem.hidden) ? 0.4 : 1,
                              transition: 'opacity 0.2s',
                              cursor: (characterId === ownerId) ? 'pointer' : 'default'
                            }}>
                              <div style={{ fontSize: `${12 * avatarScale}px`, color: '#fff', marginBottom: '4px' }}>{entry.name}</div>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editingCustomEntryValue}
                                  onChange={(e) => setEditingCustomEntryValue(e.target.value)}
                                  onBlur={handleSaveCustomEntry}
                                  onFocus={(e) => e.target.select()}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveCustomEntry();
                                    if (e.key === 'Escape') {
                                      setEditingCustomEntryId(null);
                                      setEditingCustomEntryValue('');
                                    }
                                  }}
                                  autoFocus
                                  style={{
                                    width: '50px',
                                    fontSize: `${20 * avatarScale}px`,
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    color: healthColor,
                                    background: '#222',
                                    border: `1px solid ${healthColor}`
                                  }}
                                />
                              ) : (
                                <div style={{ fontSize: `${20 * avatarScale}px`, fontWeight: 'bold', color: healthColor }}>
                                  {entry.initiative}
                                </div>
                              )}
                              {characterId === ownerId && (
                                <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', marginTop: '4px' }}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleUpdateHealthStatus(entry.id, 'healthy');
                                    }}
                                    style={{
                                      width: '20px',
                                      height: '20px',
                                      background: '#4caf50',
                                      border: entry.health_status === 'healthy' ? '2px solid white' : 'none',
                                      borderRadius: '3px',
                                      cursor: 'pointer',
                                      padding: 0
                                    }}
                                    title="Healthy (>50% HP)"
                                  />
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleUpdateHealthStatus(entry.id, 'bloodied');
                                    }}
                                    style={{
                                      width: '20px',
                                      height: '20px',
                                      background: '#ffeb3b',
                                      border: entry.health_status === 'bloodied' ? '2px solid white' : 'none',
                                      borderRadius: '3px',
                                      cursor: 'pointer',
                                      padding: 0
                                    }}
                                    title="Bloodied (50%-10% HP)"
                                  />
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleUpdateHealthStatus(entry.id, 'ravaged');
                                    }}
                                    style={{
                                      width: '20px',
                                      height: '20px',
                                      background: '#f44336',
                                      border: entry.health_status === 'ravaged' ? '2px solid white' : 'none',
                                      borderRadius: '3px',
                                      cursor: 'pointer',
                                      padding: 0
                                    }}
                                    title="Ravaged (<10% HP)"
                                  />
                                </div>
                              )}
                              {characterId === ownerId && (
                                <button
                                  onClick={() => handleDeleteCustomEntry(entry.id)}
                                  style={{
                                    position: 'absolute',
                                    top: '4px',
                                    right: '4px',
                                    background: '#d32f2f',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '20px',
                                    height: '20px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    lineHeight: '1'
                                  }}
                                  title="Delete"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    )
                  }

                  return null;
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)}>
        <DialogTitle>Add Custom Initiative Entry</DialogTitle>
        <DialogContent>
          {newEntryError && (
            <div style={{ color: '#f44336', marginBottom: '16px', padding: '8px', backgroundColor: '#ffebee', borderRadius: '4px' }}>
              {newEntryError}
            </div>
          )}
          <TextField
            label="Name"
            value={newEntryName}
            onChange={(e) => setNewEntryName(e.target.value)}
            onKeyDown={(e) => handleDialogKeyDown(e, 'name')}
            inputRef={nameFieldRef}
            fullWidth
            margin="normal"
            autoFocus
          />
          <TextField
            label="Quantity"
            type="number"
            value={newEntryQuantity}
            onChange={(e) => setNewEntryQuantity(parseInt(e.target.value) || 1)}
            onKeyDown={(e) => handleDialogKeyDown(e, 'quantity')}
            inputRef={quantityFieldRef}
            fullWidth
            margin="normal"
            inputProps={{ min: 1, max: 20 }}
          />
          <TextField
            label="Initiative"
            value={newEntryInitiative}
            onChange={(e) => setNewEntryInitiative(e.target.value)}
            onKeyDown={(e) => handleDialogKeyDown(e, 'initiative')}
            inputRef={initiativeFieldRef}
            fullWidth
            margin="normal"
            placeholder="e.g., 15 or 1d20+3"
          />
          <TextField
            label="Icon URL (optional)"
            value={newEntryIcon}
            onChange={(e) => setNewEntryIcon(e.target.value)}
            onKeyDown={(e) => handleDialogKeyDown(e, 'icon')}
            inputRef={iconFieldRef}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Notes (optional)"
            value={newEntryNotes}
            onChange={(e) => setNewEntryNotes(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateCustomEntry} color="primary" disabled={!newEntryName}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

const mapDispatchToProps = { reorderCharacters, reorderInitiativeQueueItems, updateCharacter, updateInitiativeQueue }

const mapStateToProps = (state /*, ownProps*/) => {
  return {
    characters: state.characters.list,
    ownerId: state.ownerId,
    token: state.client.token,
    gameId: state.game.id,
    initiativeQueueItems: state.initiativeQueueItems,
    initiativeQueue: state.initiativeQueue,
    isOwner: state.user.is_owner,
    characterId: state.characterId,
    customInitiativeEntries: state.customInitiativeEntries || {},
    isViewer: state.client.isViewer,
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Party)
